import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  loginApi,
  verify2FAApi,
  registerApi,
  getMeApi,
  type AuthUser,
  type LoginResult,
  type WaitlistRegisterPayload,
  type WorkspaceMembership,
  type Verify2FAResponse,
} from '@/features/auth/api/authApi';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  workspaces: WorkspaceMembership[];
  activeWorkspace: WorkspaceMembership | null;
  loading: boolean;
  isAuthenticated: boolean;
  isSaasOwner: boolean;
  permissions: Set<string>;
  hasPermission: (permId: string) => boolean;
  /** Plan-based entitlements for the active workspace (gen.photo, ...). */
  planFeatures: Set<string>;
  /** True if the active workspace's plan grants the given feature key. Saas owners bypass. */
  hasPlanFeature: (featureKey: string) => boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verify2FA: (tempToken: string, payload: { code: string } | { recoveryCodes: string[] }) => Promise<Verify2FAResponse>;
  register: (payload: WaitlistRegisterPayload) => Promise<void>;
  logout: () => void;
  setActiveWorkspace: (workspaceId: string) => void;
  refreshWorkspaces: () => Promise<void>;
  /** Merge fields into the current user (e.g. after a profile/onboarding update). */
  updateUser: (patch: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthState | null>(null);

const TOKEN_KEY = 'auth_token';
const ACTIVE_WS_KEY = 'active_workspace_id';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [workspaces, setWorkspaces] = useState<WorkspaceMembership[]>([]);
  const [activeWorkspace, setActiveWorkspaceState] = useState<WorkspaceMembership | null>(null);
  const [loading, setLoading] = useState(true);

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    setWorkspaces([]);
    setActiveWorkspaceState(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ACTIVE_WS_KEY);
  }, []);

  const resolveActiveWorkspace = useCallback((wsList: WorkspaceMembership[]) => {
    const savedId = localStorage.getItem(ACTIVE_WS_KEY);
    // Prefer the saved workspace; otherwise auto-select the first so single-
    // workspace users land straight in the app (multi-workspace can still switch).
    const match = wsList.find((w) => w.workspaceId === savedId) ?? wsList[0] ?? null;
    setActiveWorkspaceState(match);
    if (match) localStorage.setItem(ACTIVE_WS_KEY, match.workspaceId);
  }, []);

  const finalizeLogin = useCallback((data: { user: AuthUser; workspaces: WorkspaceMembership[]; token: string }) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    setWorkspaces(data.workspaces);
    resolveActiveWorkspace(data.workspaces);
  }, [resolveActiveWorkspace]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    getMeApi()
      .then((data) => {
        setUser(data.user);
        setWorkspaces(data.workspaces);
        resolveActiveWorkspace(data.workspaces);
      })
      .catch(() => {
        clearAuth();
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const result = await loginApi(email, password);
    if (!result.twoFactorRequired) {
      finalizeLogin(result);
    }
    return result;
  }, [finalizeLogin]);

  const verify2FA = useCallback(async (tempToken: string, payload: { code: string } | { recoveryCodes: string[] }): Promise<Verify2FAResponse> => {
    const data = await verify2FAApi(tempToken, payload);
    finalizeLogin(data);
    return data;
  }, [finalizeLogin]);

  const register = useCallback(async (payload: WaitlistRegisterPayload) => {
    const data = await registerApi(payload);
    finalizeLogin(data);
  }, [finalizeLogin]);

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const setActiveWorkspace = useCallback((workspaceId: string) => {
    const ws = workspaces.find((w) => w.workspaceId === workspaceId);
    if (ws) {
      setActiveWorkspaceState(ws);
      localStorage.setItem(ACTIVE_WS_KEY, workspaceId);
    }
  }, [workspaces]);

  const refreshWorkspaces = useCallback(async () => {
    const data = await getMeApi();
    setUser(data.user);
    setWorkspaces(data.workspaces);
    resolveActiveWorkspace(data.workspaces);
  }, [resolveActiveWorkspace]);

  const updateUser = useCallback((patch: Partial<AuthUser>) => {
    setUser((u) => (u ? { ...u, ...patch } : u));
  }, []);

  const permissions = useMemo<Set<string>>(() => {
    if (user?.role === 'saas_owner') return new Set(['*']);
    return new Set(activeWorkspace?.permissions ?? []);
  }, [user?.role, activeWorkspace]);

  const hasPermission = useCallback(
    (permId: string) => permissions.has('*') || permissions.has(permId),
    [permissions],
  );

  const planFeatures = useMemo<Set<string>>(
    () => new Set(activeWorkspace?.planFeatures ?? []),
    [activeWorkspace],
  );

  const hasPlanFeature = useCallback(
    (featureKey: string) => user?.role === 'saas_owner' || planFeatures.has(featureKey),
    [user?.role, planFeatures],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      token,
      workspaces,
      activeWorkspace,
      loading,
      isAuthenticated: !!user && !!token,
      isSaasOwner: user?.role === 'saas_owner',
      permissions,
      hasPermission,
      planFeatures,
      hasPlanFeature,
      login,
      verify2FA,
      register,
      logout,
      setActiveWorkspace,
      refreshWorkspaces,
      updateUser,
    }),
    [user, token, workspaces, activeWorkspace, loading, permissions, hasPermission, planFeatures, hasPlanFeature, login, verify2FA, register, logout, setActiveWorkspace, refreshWorkspaces, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
